import {MediaCardType} from '@wandb/weave/common/types/media';

import {RampKey} from '../../../../util/useRampFlag';
import {MediaConfigWithDefaults} from '../../../PanelMediaBrowser/Component';
import {MAX_COL_COUNT, MIN_COL_COUNT} from '../../../PanelMediaBrowser/config';
import {BoundingBoxes} from './components/boundingBoxes/BoundingBoxes';
import {ChartTitle} from './components/ChartTitle';
import {ColumnContent} from './components/ColumnContent';
import {FitImage} from './components/FitImage';
import {GridMode} from './components/GridMode';
import {Masks} from './components/masks/Masks';
import {MaxItemCount} from './components/MaxItemCount';
import {MaxRowCount} from './components/MaxRowCount';
import {MaxRuns} from './components/MaxRuns';
import {MediaKeys} from './components/MediaKeys';
import {NumberOfColumns} from './components/NumberOfColumns';
import {AxesHandedness} from './components/object3D/AxesHandedness';
import {Object3DBackgroundColor} from './components/object3D/Object3DBackgroundColor';
import {PointColor} from './components/object3D/PointColor';
import {OriginalSize} from './components/OriginalSize';
import {SliderKey} from './components/SliderKey';
import {SmoothImage} from './components/SmoothImage';
import {SnapToExistingStep} from './components/SnapToExistingStep';
import {StrideLength} from './components/StrideLength';
import {VideoPlaybackSync} from './components/VideoPlaybackSync';
import {VideoPlaybackSyncAutoplay} from './components/VideoPlaybackSyncAutoplay';
import {VideoPlaybackSyncLoop} from './components/VideoPlaybackSyncLoop';
import {XAxis} from './components/XAxis';
import {YAxis} from './components/YAxis';
import {SliderSync} from './SliderSync';
import {
  MediaSetting,
  MediaSettingsSection,
  MediaSettingsTab,
  SettingsLevel,
} from './types';

export const MEDIA_SETTINGS_TABS: MediaSettingsTab[] = [
  {
    title: 'Display',
    sections: [
      {
        title: 'Viewer settings',
        settings: [
          {
            component: ChartTitle,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            settingsLevels: [SettingsLevel.PANEL],
            configKey: 'chartTitle',
          },
          {
            component: MediaKeys,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            settingsLevels: [
              SettingsLevel.PANEL,
              // every panel will have a media key already defined, so setting it on a higher level does nothing.
            ],
            configKey: 'mediaKeys',
          },
          {
            component: SliderKey,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'mediaSliderKey',
          },
          {
            component: Object3DBackgroundColor,
            mediaTypes: ['object3D'],
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'object3DBackgroundColor',
          },
        ],
      },
      {
        title: 'Appearance',
        settings: [
          {
            component: SmoothImage,
            mediaTypes: ['image'],
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'pixelated',
            getComponentKey: config => `${config.pixelated.toString()}`,
          },
          {
            component: PointColor,
            mediaTypes: ['object3D'],
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'pointColor',
          },
        ],
      },
      {
        title: 'Navigation & playback',
        settings: [
          {
            component: SnapToExistingStep,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'snapToExistingStep',
            getComponentKey: config =>
              `${config.snapToExistingStep.toString()}`,
          },
          {
            component: StrideLength,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'stepStrideLength',
            getComponentKey: config => `${config.stepStrideLength?.toString()}`,
          },
        ],
      },
    ],
  },
  {
    title: 'Layout',
    sections: [
      {
        title: 'Grid & organization',
        settings: [
          {
            component: GridMode,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShow: config => !config.actualSize && !config.fitToDimension, // Grid mode only makes sense if the images are already automatically sized (not original or fit to one dimension)
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'mode',
            getComponentKey: config => config.mode,
          },
          {
            component: NumberOfColumns,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShow: config =>
              MAX_COL_COUNT > MIN_COL_COUNT && // There's no point in showing the slider if there's not more than one column number option
              !config.actualSize && // actualSize is defaulted to false in `getConfigWithDefaults` so we only care if the user has manually toggled it to true
              !config.fitToDimension, // same for fitToDimension
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'columnCount',
            getComponentKey: config => `${config.columnCount?.toString()}`,
          },
          {
            component: ColumnContent,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShow: config => config.mode === 'gallery',
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'gallerySettings',
          },
          {
            component: XAxis,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShow: config => config.mode === 'grid',
            getComponentKey: config =>
              `${config.gridSettings.xAxis},${config.gridSettings.yAxis}`, // they're interdependent since we swap them if you select the same axis for both
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'gridSettings',
          },
          {
            component: YAxis,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShow: config => config.mode === 'grid',
            getComponentKey: config =>
              `${config.gridSettings.xAxis},${config.gridSettings.yAxis}`, // ditto
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'gridSettings',
          },
          {
            component: MaxRuns,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'maxRuns',
            getComponentKey: config => `${config.maxRuns}`,
          },
          {
            component: MaxItemCount,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShow: config =>
              MAX_COL_COUNT > MIN_COL_COUNT && // There's no point in showing the slider if there's not more than one column number option
              !config.actualSize && // actualSize is defaulted to false in `getConfigWithDefaults` so we only care if the user has manually toggled it to true
              !config.fitToDimension && // same for fitToDimension
              config.mode === 'gallery',
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'maxGalleryItems',
            getComponentKey: config => `${config.maxGalleryItems?.toString()}`,
          },
          {
            component: MaxRowCount,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            shouldShow: config =>
              MAX_COL_COUNT > MIN_COL_COUNT && // There's no point in showing the slider if there's not more than one column number option
              !config.actualSize && // actualSize is defaulted to false in `getConfigWithDefaults` so we only care if the user has manually toggled it to true
              !config.fitToDimension && // same for fitToDimension
              config.mode === 'grid', // this is the grid version of MaxItemCount
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'maxYAxisCount',
            getComponentKey: config => `${config.maxYAxisCount.toString()}`,
          },
        ],
      },
      {
        title: 'Fit & alignment',
        settings: [
          {
            component: OriginalSize,
            mediaTypes: ['image', 'video'],
            shouldShow: config => config.mode !== 'grid',
            getComponentKey: config =>
              `${config.actualSize.toString()},${config.fitToDimension.toString()}`, // These settings are interdependent so we need to re-mount the component whenever either of them changes
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'actualSize',
          },
          {
            component: FitImage,
            mediaTypes: ['image'],
            shouldShow: config => config.mode !== 'grid',
            getComponentKey: config =>
              `${config.actualSize.toString()},${config.fitToDimension.toString()}`, // Ditto
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'fitToDimension',
          },
          {
            component: AxesHandedness,
            mediaTypes: ['object3D'],
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'useRightHandedSystem',
            getComponentKey: config =>
              `${config.useRightHandedSystem?.toString()}`,
          },
        ],
      },
    ],
  },
  {
    title: 'Sync',
    sections: [
      {
        title: 'Slider sync',
        settings: [
          {
            component: SliderSync,
            mediaTypes: ['image', 'video', 'audio', 'object3D'],
            settingsLevels: [
              // Slider sync is not supported in the panel settings because what would the slider sync with?
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'sliderSync',
            getComponentKey: config =>
              `${config.sliderSync?.toString()},${config.mediaSliderKey?.toString()}`,
          },
        ],
      },
      {
        title: 'Sync video playback',
        settings: [
          {
            component: VideoPlaybackSync,
            mediaTypes: ['video'],
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            shouldShow: (config, _hasMasks, _hasBoundingBoxes, rampFlags) => {
              return rampFlags?.['media_video_sync_enabled_user'] ?? false;
            },
            configKey: 'videoPlaybackSync',
            getComponentKey: config =>
              `${config.videoPlaybackSync?.toString()}`,
            subsettings: [
              {
                component: VideoPlaybackSyncAutoplay,
                mediaTypes: ['video'],
                settingsLevels: [
                  SettingsLevel.PANEL,
                  SettingsLevel.SECTION,
                  SettingsLevel.WORKSPACE,
                ],
                shouldShow: (
                  config,
                  _hasMasks,
                  _hasBoundingBoxes,
                  rampFlags
                ) => {
                  const isFeatureEnabled =
                    rampFlags?.['media_video_sync_enabled_user'] ?? false;
                  const isSyncEnabled = config.videoPlaybackSync ?? false;
                  return isFeatureEnabled && isSyncEnabled;
                },
                configKey: 'videoPlaybackSyncAutoplay',
                getComponentKey: config =>
                  `${config.videoPlaybackSyncAutoplay?.toString()}`,
              },
              {
                component: VideoPlaybackSyncLoop,
                mediaTypes: ['video'],
                settingsLevels: [
                  SettingsLevel.PANEL,
                  SettingsLevel.SECTION,
                  SettingsLevel.WORKSPACE,
                ],
                shouldShow: (
                  config,
                  _hasMasks,
                  _hasBoundingBoxes,
                  rampFlags
                ) => {
                  const isFeatureEnabled =
                    rampFlags?.['media_video_sync_enabled_user'] ?? false;
                  const isSyncEnabled = config.videoPlaybackSync ?? false;
                  return isFeatureEnabled && isSyncEnabled;
                },
                configKey: 'videoPlaybackSyncLoop',
                getComponentKey: config =>
                  `${config.videoPlaybackSyncLoop?.toString()}`,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    title: 'Overlays',
    sections: [
      {
        title: 'Bounding boxes',
        settings: [
          {
            component: BoundingBoxes,
            mediaTypes: ['image', 'object3D'],
            shouldShow: (_config, _hasMasks, hasBoundingBoxes) =>
              hasBoundingBoxes,
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'boundingBoxConfig',
          },
        ],
      },
      {
        title: 'Masks',
        settings: [
          {
            component: Masks,
            mediaTypes: ['image'],
            shouldShow: (_config, hasMasks, _hasBoundingBoxes) => hasMasks,
            settingsLevels: [
              SettingsLevel.PANEL,
              SettingsLevel.SECTION,
              SettingsLevel.WORKSPACE,
            ],
            configKey: 'segmentationMaskConfig',
          },
        ],
      },
    ],
  },
];

export function getMediaSettingsTabs(
  mediaTypes: MediaCardType[] | null,
  config: MediaConfigWithDefaults,
  hasMasks: boolean,
  hasBoundingBoxes: boolean,
  settingsLevel: SettingsLevel,
  rampFlags?: Partial<Record<RampKey, boolean>> | undefined
): MediaSettingsTab[] {
  if (mediaTypes == null) {
    return [];
  }

  return MEDIA_SETTINGS_TABS.map(tab => ({
    ...tab,
    sections: filterSections(
      tab.sections,
      mediaTypes,
      config,
      hasMasks,
      hasBoundingBoxes,
      rampFlags,
      settingsLevel
    ),
  })).filter(tab => tab.sections.length > 0);
}

function filterSections(
  sections: MediaSettingsSection[],
  mediaTypes: MediaCardType[],
  config: MediaConfigWithDefaults,
  hasMasks: boolean,
  hasBoundingBoxes: boolean,
  rampFlags: Partial<Record<RampKey, boolean>> | undefined,
  settingsLevel: SettingsLevel
): MediaSettingsSection[] {
  return sections
    .map(section => ({
      ...section,
      settings: filterSettings(
        section.settings,
        mediaTypes,
        config,
        hasMasks,
        hasBoundingBoxes,
        rampFlags,
        settingsLevel
      ),
    }))
    .filter(section => section.settings.length > 0);
}

function filterSettings(
  settings: MediaSetting[],
  mediaTypes: MediaCardType[],
  config: MediaConfigWithDefaults,
  hasMasks: boolean,
  hasBoundingBoxes: boolean,
  rampFlags: Partial<Record<RampKey, boolean>> | undefined,
  settingsLevel: SettingsLevel
): MediaSetting[] {
  return settings
    .filter(setting =>
      shouldShowSetting(
        setting,
        mediaTypes,
        config,
        hasMasks,
        hasBoundingBoxes,
        rampFlags,
        settingsLevel
      )
    )
    .map(setting => {
      if (setting.subsettings) {
        return {
          ...setting,
          subsettings: filterSettings(
            setting.subsettings,
            mediaTypes,
            config,
            hasMasks,
            hasBoundingBoxes,
            rampFlags,
            settingsLevel
          ),
        };
      }
      return setting;
    });
}
function shouldShowSetting(
  setting: MediaSetting,
  mediaTypes: MediaCardType[],
  config: MediaConfigWithDefaults,
  hasMasks: boolean,
  hasBoundingBoxes: boolean,
  rampFlags: Partial<Record<RampKey, boolean>> | undefined,
  settingsLevel: SettingsLevel
) {
  const isRelevantSettingForMediaTypes = setting.mediaTypes.some(mediaType =>
    mediaTypes.includes(mediaType)
  );
  const isRelevantSettingForCustomShouldShow =
    setting.shouldShow?.(config, hasMasks, hasBoundingBoxes, rampFlags) ?? true;
  const isRelevantSettingForSettingsLevel =
    setting.settingsLevels.includes(settingsLevel);
  const shouldShow =
    isRelevantSettingForMediaTypes &&
    isRelevantSettingForCustomShouldShow &&
    isRelevantSettingForSettingsLevel;
  return shouldShow;
}
